import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Loader2, Calendar, Clock, MapPin, PoundSterling,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatGBP } from '@/lib/utils';

function nextHourIso() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 2);
  // Snap into permitted booking window (06:00–22:59 UK local).
  if (d.getHours() < 6) d.setHours(9);
  if (d.getHours() >= 23) {
    d.setDate(d.getDate() + 1);
    d.setHours(9);
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Booking-hours safety rule mirrored from backend (Europe/London 06:00–23:00).
// Minute-precision: 06:00 allowed, 22:59 allowed, 23:00 blocked, 05:59 blocked.
const BOOKING_HOUR_START_MIN = 6 * 60;
const BOOKING_HOUR_END_MIN = 23 * 60;
const BOOKING_HOURS_ERROR =
  'Bookings are only available between 06:00 and 23:00 UK time for safety reasons.';

function isWithinBookingHours(value) {
  if (!value) return false;
  // datetime-local has no timezone — interpret literally as UK local time
  // (matches how the backend parses it).
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return false;
  const minutes = Number(match[4]) * 60 + Number(match[5]);
  return minutes >= BOOKING_HOUR_START_MIN && minutes < BOOKING_HOUR_END_MIN;
}

export default function CustomerBooking() {
  const { partnerId, serviceId } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [service, setService] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('HA1');
  const [scheduledAt, setScheduledAt] = useState(nextHourIso());
  const [notes, setNotes] = useState('');
  const [travelFee, setTravelFee] = useState(5);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get(`/customer/partners/${partnerId}`),
        api.get('/customer/me'),
      ]);
      setPartner(p.data);
      const svc = p.data.services.find((s) => s.id === serviceId);
      setService(svc || null);
      setMe(c.data);
      if (c.data?.default_address) setAddress(c.data.default_address);
      if (c.data?.postcode) setPostcode(c.data.postcode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  }, [partnerId, serviceId]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    if (!service) return null;
    const sp = service.price;
    const tf = parseFloat(travelFee || 0);
    return {
      service_price: sp,
      travel_fee: tf,
      total: sp + tf,
      commission: sp * 0.2,
    };
  }, [service, travelFee]);

  const handleConfirm = useCallback(async () => {
    if (!address.trim()) {
      toast.error('Please enter your address');
      return;
    }
    if (!isWithinBookingHours(scheduledAt)) {
      toast.error(BOOKING_HOURS_ERROR);
      return;
    }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post('/customer/bookings', {
        partner_id: partnerId,
        service_id: serviceId,
        address: address.trim(),
        postcode: postcode.trim().toUpperCase(),
        scheduled_at: scheduledAt,
        travel_fee: parseFloat(travelFee || 0),
        notes: notes.trim(),
      });
      toast.success('Booking created');
      // Immediately start checkout
      try {
        const { data: ck } = await api.post(`/bookings/${booking.id}/checkout`);
        if (ck.checkout_url) {
          if (ck.simulated) {
            // Internal route -> use React-Router-style nav
            navigate(`/customer/payment/success?booking_id=${booking.id}&demo=1`);
          } else {
            window.location.href = ck.checkout_url;
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not start checkout');
        navigate('/customer/bookings');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  }, [partnerId, serviceId, address, postcode, scheduledAt, travelFee, notes, navigate]);

  if (loading) {
    return <p className="text-center text-sm text-slate-500 py-10"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading...</p>;
  }
  if (!partner || !service) {
    return <p className="text-center text-sm text-rose-600 py-10">Service not available</p>;
  }

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="booking-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <Card className="p-4 border-slate-200">
        <p className="text-[11px] uppercase tracking-wider text-slate-400">Booking with</p>
        <p className="text-sm font-semibold text-slate-900">{partner.business_name}</p>
        <p className="text-xs text-slate-500 mt-1">{service.name} · {service.duration_minutes} min</p>
      </Card>

      <Card className="p-4 border-slate-200 space-y-3">
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> Date &amp; time</Label>
          <Input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="h-11"
            data-testid="booking-datetime"
            min="2020-01-01T06:00"
          />
          <p
            className={`text-[11px] mt-1 ${isWithinBookingHours(scheduledAt) ? 'text-slate-500' : 'text-rose-700 font-semibold'}`}
            data-testid="booking-hours-note"
          >
            <Clock className="h-3 w-3 inline mr-0.5" /> Permitted booking hours: 06:00 — 23:00 UK local time.
            {!isWithinBookingHours(scheduledAt) && (
              <span className="block mt-1">{BOOKING_HOURS_ERROR}</span>
            )}
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</Label>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="h-11"
            placeholder="e.g. 12 High Street, Harrow"
            required
            data-testid="booking-address"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Postcode</Label>
          <Input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            className="h-11 font-mono"
            maxLength={8}
            data-testid="booking-postcode"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Notes for pro (optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything they should know?"
            data-testid="booking-notes"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Travel fee (£)</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={travelFee}
            onChange={(e) => setTravelFee(e.target.value)}
            className="h-11"
            data-testid="booking-travel"
          />
        </div>
      </Card>

      {totals && (
        <Card className="p-4 border-slate-200 space-y-2">
          <p className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><PoundSterling className="h-3 w-3" /> Summary</p>
          <div className="text-sm flex justify-between"><span>Service</span><span>{formatGBP(totals.service_price)}</span></div>
          <div className="text-sm flex justify-between"><span>Travel</span><span>{formatGBP(totals.travel_fee)}</span></div>
          <div className="text-sm flex justify-between"><span>Commission (20%)</span><span className="text-rose-600">{formatGBP(totals.commission)}</span></div>
          <div className="h-px bg-slate-100" />
          <div className="text-base font-bold flex justify-between text-slate-900"><span>You pay</span><span>{formatGBP(totals.total)}</span></div>
        </Card>
      )}

      <Button
        onClick={handleConfirm}
        disabled={submitting || !isWithinBookingHours(scheduledAt)}
        data-testid="booking-confirm"
        className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base disabled:opacity-50"
      >
        {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Confirming...</> : 'Confirm & pay'}
      </Button>
      <p className="text-[11px] text-center text-slate-500 leading-relaxed" data-testid="booking-terms-reminder">
        By confirming this booking, you agree that the service is provided by an independent
        BeautyMateUK Partner and must take place between 06:00 and 23:00.
      </p>
      <p className="text-[11px] text-center text-slate-400">Payment is processed securely via Stripe.</p>
    </div>
  );
}
